import {useMemo} from 'react';
import {kr, tickLabels} from '../../lib/format.js';
import {DASHES, niceScale, Grid, useHover, HitBands, Tooltip, Legend} from './primitives.jsx';

const W = 560, H = 300, L = 56, R = 10, T = 10, B = 24;

/**
 * A multi-line balance chart — outgoing balance per month, walked from the vouchers.
 * Used for both receivables/payables and the bank accounts, because they are the same
 * shape of question: "what was the closing balance of these accounts, month by month".
 *
 * Series identity is dash pattern plus marker fill, never colour. Past eight series a
 * dash pattern stops being distinguishable, so the caller must fold the tail into an
 * "Övriga" line rather than adding a ninth.
 */
export default function BalanceLines({title, hint, months, series}) {
	const {at, enter, leave} = useHover();
	const ticks = useMemo(() => tickLabels(months), [months]);
	const g = useMemo(() => {
		const vals = months.flatMap(m => series.map(s => m[s.key] ?? 0));
		const sc = niceScale(Math.min(0, ...vals), Math.max(0, ...vals));
		const iw = W - L - R, ih = H - T - B;
		return {
			sc,
			y: v => T + ih - ((v - sc.lo) / (sc.hi - sc.lo)) * ih,
			x: i => L + (iw * (i + 0.5)) / months.length,
		};
	}, [months, series]);

	if (!months.length) return null;

	return (
		<div className="chart">
			<div className="chart__head">
				<span className="chart__title">{title}</span>
				<span className="chart__hint">{hint}</span>
			</div>
			<div className="chart__canvas">
				<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={title}>
					<Grid {...g.sc} y={g.y} L={L} R={R} W={W} />
					{series.map((s, si) => (
						<polyline key={s.key} fill="none" stroke="#000"
							strokeWidth={s.emphasis ? 2.2 : 1.6}
							strokeDasharray={(s.dash ?? DASHES[si % DASHES.length]) ?? undefined}
							strokeLinejoin="round"
							points={months.map((m, i) => (m[s.key] == null ? null : `${g.x(i)},${g.y(m[s.key])}`))
								.filter(Boolean).join(' ')} />
					))}
					{months.length <= 20 && series.map((s, si) => months.map((m, i) => (m[s.key] == null ? null : (
						<circle key={`${s.key}-${m.month}`} cx={g.x(i)} cy={g.y(m[s.key])} r={2.4}
							fill={si % 2 ? '#fff' : '#000'} stroke="#000" strokeWidth={1.1} />
					))))}
					{ticks.map((t, i) => (t == null ? null : (
						<text key={months[i].month} x={g.x(i)} y={H - 8} className="axs" textAnchor="middle">{t}</text>
					)))}
					<HitBands months={months} x={g.x} W={W} H={H} onEnter={enter} onLeave={leave} />
				</svg>
				<Tooltip months={months} at={at} x={g.x} W={W} title={m => m.label ?? m.month}
					rows={m => series.map(s => [s.label, `${kr(m[s.key])} kr`])} />
			</div>
			<Legend items={series.map((s, si) => ({
				label: s.label, dash: s.dash ?? DASHES[si % DASHES.length],
			}))} />
		</div>
	);
}
